import { ORG_ROLE_PERMISSIONS, STAFF_ESSENTIALS } from "./roles";
import { ALL_PERMISSIONS, permissionsFor, splitPermission, type Permission } from "./statement";

/** Platform team roles, written as permission sets like a tenant's. `platform` is what a role may
 * do at the console outside every organization; `tenant` is what it acts with inside a customer's
 * organization, in place of a membership. */
export const PLATFORM_ROLES = ["admin", "support"] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export interface PlatformRoleDefinition {
  id: PlatformRole;
  name: string;
  description: string;
  platform: readonly Permission[];
  tenant: readonly Permission[];
}

/** Everything that only looks: every `read` action, plus what a login needs to find itself. */
const READ_ONLY: Permission[] = [
  ...new Set([
    ...ALL_PERMISSIONS.filter((permission) => splitPermission(permission).action === "read"),
    ...STAFF_ESSENTIALS,
  ]),
];

export const PLATFORM_ROLE_DEFINITIONS: Readonly<Record<PlatformRole, PlatformRoleDefinition>> = {
  admin: {
    id: "admin",
    name: "Admin",
    description:
      "Runs the platform: creates organizations, changes plans and overrides, manages the platform team, and acts as the owner inside any organization it enters.",
    platform: permissionsFor("platform"),
    tenant: ORG_ROLE_PERMISSIONS.owner,
  },
  support: {
    id: "support",
    name: "Support",
    description:
      "Helps customers: sees the tenant list and the console, enters an organization to look at every screen as its owner would, and changes nothing there.",
    platform: ["platform:read", ...STAFF_ESSENTIALS],
    tenant: READ_ONLY,
  },
};

export function isPlatformRole(value: string): value is PlatformRole {
  return (PLATFORM_ROLES as readonly string[]).includes(value);
}
